/**
 * Reusable Figma-styled toolbar for oasis {@link PdfRenderer}.
 *
 * Layout: optional leading slot, always-open search (with a match count and
 * previous / next match buttons, mirroring Enter and Shift+Enter), zoom controls, a divider,
 * a page-rotation button, and optional thumbs-up / thumbs-down feedback.
 * Built-in page navigation is omitted. Rotation is owned by the parent (oasis
 * exposes no rotate API); this component only surfaces the button and reflects
 * the current angle.
 *
 * Callers inject feature-specific leftmost controls via `leadingContent` (e.g.
 * a document index) and optional search analytics via `onSearchSubmitted`.
 * Feedback is off by default (`showFeedback`) so screens like Medical Necessity
 * keep a toolbar without rating controls.
 * @module OasisPdfToolbar
 */
import { PdfToolbarContext } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { ThumbsDownIcon } from "../../svg/thumbs-down-icon";
import { ThumbsUpIcon } from "../../svg/thumbs-up-icon";

/** Debounce before invoking `onSearchSubmitted`. */
const PDF_SEARCH_ANALYTICS_DEBOUNCE_MS = 500;

/** Props for the feather-style stroke icons. */
interface IconProps {
  size?: number;
}

/** Zoom-out magnifier (feather `zoom-out`). */
const ZoomOut: React.FC<IconProps> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35M8 11h6" />
  </svg>
);

/** Zoom-in magnifier (feather `zoom-in`). */
const ZoomIn: React.FC<IconProps> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
  </svg>
);

/** Search magnifier (feather `search`). */
const SearchIcon: React.FC<IconProps & { className?: string }> = ({
  size = 16,
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

/** Counter-clockwise rotate arrow (feather `rotate-ccw`). */
const RotateIcon: React.FC<IconProps> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

/** Chevron used for the previous / next search-match buttons (feather `chevron-up`). */
const ChevronIcon: React.FC<IconProps & { direction: "up" | "down" }> = ({
  size = 14,
  direction,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={direction === "down" ? { transform: "rotate(180deg)" } : undefined}
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

/** Full-width wrapper that lets the search box grow to fill the toolbar. */
const SEARCH_GROUP_STYLE: CSSProperties = { flex: 1, minWidth: 0 };

/** Search field container (bordered, rounded box mirroring the Figma spec). */
const SEARCH_BOX_STYLE: CSSProperties = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "100%",
};

/** Search text input styles (right padding is widened while a query is active). */
const SEARCH_INPUT_STYLE: CSSProperties = {
  width: "100%",
  height: "36px",
  padding: "0 40px 0 12px",
  border: "1px solid #D6D6D6",
  borderRadius: "6px",
  outline: "none",
  background: "#fff",
  fontFamily: "inherit",
  fontSize: "13px",
  lineHeight: "18px",
  color: "#0F0F0F",
};

/** Right padding that keeps typed text clear of the match count and nav buttons. */
const SEARCH_INPUT_ACTIVE_PADDING_RIGHT = "126px";

/** Cluster holding the match count and the previous / next match buttons. */
const SEARCH_CONTROLS_STYLE: CSSProperties = {
  position: "absolute",
  right: "34px",
  display: "flex",
  alignItems: "center",
  gap: "2px",
};

/** Match-count badge shown inside the search box, left of the nav buttons. */
const SEARCH_COUNT_STYLE: CSSProperties = {
  whiteSpace: "nowrap",
  fontFamily: "inherit",
  fontSize: "11px",
  lineHeight: "18px",
  color: "#6B6B6B",
};

/** Previous / next match button (background is left to the hover class). */
const SEARCH_NAV_BUTTON_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  padding: 0,
  border: "none",
  borderRadius: "4px",
  color: "#6B6B6B",
  cursor: "pointer",
};

/** Hover affordance for the enabled previous / next match buttons. */
const SEARCH_NAV_BUTTON_CLASS =
  "bg-transparent transition-colors hover:bg-primaryGray-15";

/** Previous / next match button while there is nothing to cycle through. */
const SEARCH_NAV_BUTTON_DISABLED_STYLE: CSSProperties = {
  ...SEARCH_NAV_BUTTON_STYLE,
  color: "#C4C4C4",
  cursor: "default",
};

/** Search icon anchored to the right edge of the search box. */
const SEARCH_ICON_STYLE: CSSProperties = {
  position: "absolute",
  right: "12px",
  color: "#6B6B6B",
  pointerEvents: "none",
};

/** Thin vertical divider separating the zoom and rotate controls. */
const DIVIDER_STYLE: CSSProperties = {
  width: "1px",
  height: "20px",
  background: "#E6E6E6",
  flexShrink: 0,
};

/** Selected thumbs-up / thumbs-down value, or null when unset. */
export type PdfToolbarFeedback = "up" | "down" | null;

/** Props for {@link OasisPdfToolbar}. */
export interface OasisPdfToolbarProps {
  /** Toolbar context supplied by the PDF renderer. */
  ctx: PdfToolbarContext;
  /** Current page rotation in degrees (0, 90, 180, 270). */
  rotation: number;
  /** Advances the page rotation by one 90° step. */
  onRotate: () => void;
  /** Optional leftmost control (e.g. document index). */
  leadingContent?: React.ReactNode;
  /** Optional debounced callback when a non-empty search query settles. */
  onSearchSubmitted?: (query: string) => void;
  /**
   * When true, renders thumbs-up / thumbs-down after rotate. Off by default so
   * Medical Necessity can omit them.
   */
  showFeedback?: boolean;
  /** Controlled feedback selection; omit to let the toolbar keep local state. */
  feedback?: PdfToolbarFeedback;
  /** Invoked when the reviewer clicks a feedback thumb. */
  onFeedback?: (feedback: PdfToolbarFeedback) => void;
}

/**
 * Renders the shared oasis PDF toolbar.
 *
 * @input props - Renderer context, rotation, optional leading slot, search, and feedback
 * @return The rendered toolbar
 */
export const OasisPdfToolbar: React.FC<OasisPdfToolbarProps> = ({
  ctx,
  rotation,
  onRotate,
  leadingContent,
  onSearchSubmitted,
  showFeedback = false,
  feedback,
  onFeedback,
}) => {
  const { features } = ctx;
  const [query, setQuery] = useState("");
  const [internalFeedback, setInternalFeedback] =
    useState<PdfToolbarFeedback>(null);
  const selectedFeedback = feedback !== undefined ? feedback : internalFeedback;
  const lastSubmittedQueryRef = useRef("");
  const searchAnalyticsTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(
    () => () => {
      if (searchAnalyticsTimeoutRef.current) {
        clearTimeout(searchAnalyticsTimeoutRef.current);
      }
    },
    [],
  );

  /**
   * Schedules a debounced search callback for a settled query.
   *
   * Skips empty queries and identical re-submits so Enter match-cycling and
   * mid-edit keystrokes do not inflate search counts.
   *
   * @input value - Raw search input value
   */
  const scheduleSearchAnalytics = (value: string) => {
    if (!onSearchSubmitted) return;
    if (searchAnalyticsTimeoutRef.current) {
      clearTimeout(searchAnalyticsTimeoutRef.current);
      searchAnalyticsTimeoutRef.current = null;
    }
    const trimmed = value.trim();
    if (!trimmed) return;
    searchAnalyticsTimeoutRef.current = setTimeout(() => {
      searchAnalyticsTimeoutRef.current = null;
      if (trimmed === lastSubmittedQueryRef.current) return;
      lastSubmittedQueryRef.current = trimmed;
      onSearchSubmitted(trimmed);
    }, PDF_SEARCH_ANALYTICS_DEBOUNCE_MS);
  };

  /**
   * Updates local query state, runs PDF search, and schedules analytics.
   *
   * @input value - New search input value
   */
  const runSearch = (value: string) => {
    setQuery(value);
    ctx.search(value);
    scheduleSearchAnalytics(value);
  };

  /** Clears the search field, PDF highlights, and pending analytics. */
  const clearSearch = () => {
    setQuery("");
    ctx.clearSearch();
    if (searchAnalyticsTimeoutRef.current) {
      clearTimeout(searchAnalyticsTimeoutRef.current);
      searchAnalyticsTimeoutRef.current = null;
    }
    lastSubmittedQueryRef.current = "";
  };

  /**
   * Toggles a feedback thumb: clicking the selected value clears it.
   *
   * @input value - The thumb that was clicked
   */
  const handleFeedback = (value: Exclude<PdfToolbarFeedback, null>) => {
    const next: PdfToolbarFeedback = selectedFeedback === value ? null : value;
    if (feedback === undefined) setInternalFeedback(next);
    onFeedback?.(next);
  };

  /**
   * Keeps the caret in the search field when a nav button is clicked.
   *
   * @input event - Mouse-down event on a previous/next match button
   */
  const keepSearchFocus = (event: React.MouseEvent<HTMLButtonElement>) =>
    event.preventDefault();

  const hasMatches = ctx.searchResults.total > 0;

  return (
    <div className="pdf-renderer__toolbar box-border !h-[3.75rem] !border-b !border-primaryGray-15">
      {leadingContent != null && (
        <div className="pdf-renderer__toolbar-group">{leadingContent}</div>
      )}

      {features.search && (
        <div className="pdf-renderer__toolbar-group" style={SEARCH_GROUP_STYLE}>
          <div style={SEARCH_BOX_STYLE}>
            <input
              className="pdf-renderer__search-input"
              style={
                query
                  ? {
                      ...SEARCH_INPUT_STYLE,
                      paddingRight: SEARCH_INPUT_ACTIVE_PADDING_RIGHT,
                    }
                  : SEARCH_INPUT_STYLE
              }
              placeholder="Search"
              value={query}
              onChange={(e) => runSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (e.shiftKey) ctx.previousSearchResult();
                  else ctx.nextSearchResult();
                }
                if (e.key === "Escape") clearSearch();
              }}
            />
            {query && (
              <div style={SEARCH_CONTROLS_STYLE}>
                <span style={SEARCH_COUNT_STYLE}>
                  {hasMatches
                    ? `${ctx.searchResults.activeResultIndex + 1}/${
                        ctx.searchResults.total
                      }`
                    : "0/0"}
                </span>
                <button
                  type="button"
                  className={hasMatches ? SEARCH_NAV_BUTTON_CLASS : undefined}
                  style={
                    hasMatches
                      ? SEARCH_NAV_BUTTON_STYLE
                      : SEARCH_NAV_BUTTON_DISABLED_STYLE
                  }
                  aria-label="Previous match"
                  title="Previous match (Shift+Enter)"
                  disabled={!hasMatches}
                  onMouseDown={keepSearchFocus}
                  onClick={ctx.previousSearchResult}
                >
                  <ChevronIcon direction="up" />
                </button>
                <button
                  type="button"
                  className={hasMatches ? SEARCH_NAV_BUTTON_CLASS : undefined}
                  style={
                    hasMatches
                      ? SEARCH_NAV_BUTTON_STYLE
                      : SEARCH_NAV_BUTTON_DISABLED_STYLE
                  }
                  aria-label="Next match"
                  title="Next match (Enter)"
                  disabled={!hasMatches}
                  onMouseDown={keepSearchFocus}
                  onClick={ctx.nextSearchResult}
                >
                  <ChevronIcon direction="down" />
                </button>
              </div>
            )}
            <span style={SEARCH_ICON_STYLE}>
              <SearchIcon size={16} />
            </span>
          </div>
        </div>
      )}

      {features.zoom && (
        <div className="pdf-renderer__toolbar-group">
          <button
            type="button"
            className="pdf-renderer__tool-btn"
            aria-label="Zoom out"
            onClick={ctx.zoomOut}
          >
            <ZoomOut />
          </button>
          <span className="pdf-renderer__zoom-value">
            {Math.round(ctx.zoom * 100)}%
          </span>
          <button
            type="button"
            className="pdf-renderer__tool-btn"
            aria-label="Zoom in"
            onClick={ctx.zoomIn}
          >
            <ZoomIn />
          </button>
        </div>
      )}

      <div style={DIVIDER_STYLE} />

      <div className="pdf-renderer__toolbar-group">
        <button
          type="button"
          className="pdf-renderer__tool-btn"
          aria-label={`Rotate page (currently ${rotation}°)`}
          onClick={onRotate}
        >
          <RotateIcon />
        </button>
      </div>

      {showFeedback && (
        <>
          <div style={DIVIDER_STYLE} />
          <div className="pdf-renderer__toolbar-group">
            <button
              type="button"
              className="pdf-renderer__tool-btn"
              aria-label="Thumbs up"
              aria-pressed={selectedFeedback === "up"}
              onClick={() => handleFeedback("up")}
            >
              <ThumbsUpIcon
                color={selectedFeedback === "up" ? "#0F0F0F" : "#6B6B6B"}
              />
            </button>
            <button
              type="button"
              className="pdf-renderer__tool-btn"
              aria-label="Thumbs down"
              aria-pressed={selectedFeedback === "down"}
              onClick={() => handleFeedback("down")}
            >
              <ThumbsDownIcon
                color={selectedFeedback === "down" ? "#0F0F0F" : "#6B6B6B"}
              />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default OasisPdfToolbar;
